/**
 * Manufacturing management → Transfers workbench.
 *
 * Hub-side workbench covering the full transfer-execution lifecycle:
 *   - planner-side `mrp.transfer_request` -> `public.transfer_order (pending)` conversion
 *   - lifecycle transitions (pending → packing → ready_to_ship → shipped → delivered → partially_received → received),
 *     including packing → pending when Start packing was pressed by mistake
 *     and Cancel from any non-terminal status
 *   - simplified packing (boxes + per-line allocations) for external inbound transfers
 *   - receive variance capture
 *   - ad-hoc transfer creation (internal site-to-site and external inbound)
 *
 * UPS label and pickup capture remain on `/shipping/outbound`; the workbench
 * deep-links there only at ready_to_ship and to `/shipping/pre-pack/transfers/:id` during packing.
 *
 * Read source: GET /api/manufacturing/transfers/board.
 * Lifecycle writes go through the new `/api/manufacturing/transfers/*` family.
 * Convert/release/edit-pending lines stay under `/api/mrp/transfer-orders` and
 * `/api/mrp/transfer-requests` since they are planner-side actions.
 * Move a pending line to another pending transfer (or a new one) via
 * POST `/api/mrp/transfer-orders/:id/lines/:lineId/move`.
 */

import {
  type ReactNode,
} from "react";
import {
  MrpSkuCatalogHoverHintBody,
} from "@/components/mrp/MrpSkuCatalogHoverHintBody";
import {
  MrpSkuWithUnitLabel,
} from "@/components/mrp/MrpSkuWithUnitLabel";
import {
  transferReasonDefault,
} from "@/components/mrp/TransferReasonSelect";
import {
  HoverHintTrigger,
} from "@/components/ui/title-help";
import {
  type MrpSkuHoverHintFields,
  type MrpTransferReason,
} from "@/lib/mrp";
import {
  cn,
} from "@/lib/utils";
import {
  TRANSFER_ORDER_STATUS_VALUES,
  TRANSFER_ORDER_TERMINAL_STATUSES,
  type TransferBoardOrder,
  type TransferBoardRequest,
  type TransferOrderStatus,
} from "./transfersWorkbenchTypes";
export const TRANSFER_CARD_ACTION_BASE =
  "inline-flex h-full min-h-0 shrink-0 self-stretch items-center justify-center rounded-md px-2.5 text-sm font-medium leading-tight";
export const TRANSFER_CARD_STACKED_ACTION_CLASS = cn(
  TRANSFER_CARD_ACTION_BASE,
  "flex-col gap-0 whitespace-normal py-1 text-center",
);
export const TRANSFER_CARD_STACKED_BUTTON_CLASS = cn(
  TRANSFER_CARD_STACKED_ACTION_CLASS,
  "!h-full !min-h-0",
);
export const TRANSFER_CARD_STACKED_LINK_CLASS = cn(
  TRANSFER_CARD_STACKED_ACTION_CLASS,
  "border border-slate-300 bg-white text-slate-900 transition-colors hover:bg-slate-100",
);

export function TransferCardStackedLabel({
  top,
  bottom,
  icon,
}: {
  top: string;
  bottom?: string;
  icon?: ReactNode;
}): JSX.Element {
  if (bottom == null || bottom === "") {
    return (
      <span className="inline-flex items-center gap-0.5 text-center">
        {icon}
        {top}
      </span>
    );
  }
  return (
    <span className="flex flex-col items-center gap-0.5 text-center leading-tight">
      <span className="inline-flex items-center gap-1">
        {icon}
        {top}
      </span>
      <span>{bottom}</span>
    </span>
  );
}

export type TransferCardActionKey =
  | "review_approvals"
  | "reprint_carton_labels"
  | "open_in_pre_pack"
  | "download_pdf"
  | "commercial_invoice"
  | "open_in_outbound"
  | "edit_tracking"
  | "start_packing"
  | "mark_ready_to_ship"
  | "mark_shipped"
  | "mark_delivered"
  | "revert_to_pending"
  | "receive_transfer"
  | "edit"
  | "cancel";

export function transferCardActionContent(
  action: TransferCardActionKey,
  options?: { icon?: ReactNode },
): ReactNode {
  switch (action) {
    case "review_approvals":
      return <TransferCardStackedLabel top="Review" bottom="approvals" />;
    case "reprint_carton_labels":
      return (
        <TransferCardStackedLabel
          top="Reprint"
          bottom="carton labels"
          icon={options?.icon}
        />
      );
    case "open_in_pre_pack":
      return (
        <TransferCardStackedLabel
          top="Open in"
          bottom="pre-pack"
          icon={options?.icon}
        />
      );
    case "edit_tracking":
      return <TransferCardStackedLabel top="Edit" bottom="tracking" />;
    case "download_pdf":
      return (
        <TransferCardStackedLabel
          top="Download"
          bottom="PDF"
          icon={options?.icon}
        />
      );
    case "commercial_invoice":
      return (
        <TransferCardStackedLabel
          top="Commercial"
          bottom="invoice"
          icon={options?.icon}
        />
      );
    case "open_in_outbound":
      return (
        <TransferCardStackedLabel
          top="Open in"
          bottom="outbound"
          icon={options?.icon}
        />
      );
    case "start_packing":
      return <TransferCardStackedLabel top="Lock for" bottom="packing" />;
    case "mark_ready_to_ship":
      return <TransferCardStackedLabel top="Mark ready" bottom="to ship" />;
    case "mark_shipped":
      return <TransferCardStackedLabel top="Mark" bottom="shipped" />;
    case "mark_delivered":
      return <TransferCardStackedLabel top="Mark" bottom="delivered" />;
    case "revert_to_pending":
      return <TransferCardStackedLabel top="Revert" bottom="to pending" />;
    case "receive_transfer":
      return <TransferCardStackedLabel top="Receive" bottom="transfer" />;
    case "edit":
      return <TransferCardStackedLabel top="Edit" />;
    case "cancel":
      return <TransferCardStackedLabel top="Cancel" />;
  }
}

export function transferCardActionKeyForPrimaryLabel(label: string): TransferCardActionKey | null {
  switch (label) {
    case "Lock for packing":
      return "start_packing";
    case "Mark ready to ship":
      return "mark_ready_to_ship";
    case "Mark shipped":
      return "mark_shipped";
    case "Mark delivered":
      return "mark_delivered";
    default:
      return null;
  }
}

export function stopTransferCardButtonClick(event: { stopPropagation: () => void; preventDefault: () => void }): void {
  event.stopPropagation();
  event.preventDefault();
}

export function stopTransferCardLinkClick(event: { stopPropagation: () => void }): void {
  event.stopPropagation();
}

export const PAGE_HOVER_HINT =
  "Run the entire transfer-execution lifecycle from one workbench: convert open transfer requests into pending orders, advance each order through packing and shipping, capture receive events (including return to origin and lost wastage), and watch Megaventory documents land back from the integrator (slice 3). SGS-origin transfers pack cartons in pre-pack, then use shipping outbound for UPS labels. Non-SGS origins (for example Sun to SGS, or TenX to SGS) skip pre-pack — download the transfer order PDF and send it to the shipper instead. If you cannot pack the full ordered quantity, submit a pack quantity change for approval — the transfer cannot move past packing until approvers accept and lines are fully allocated to the approved target. Use Create transfer for any planned move between currently active hub locations (except in-transit) or external inbound stock that nobody requested on the left.";

export interface ConvertDialogState {
  mode: "new-order" | "existing-order";
  /** Existing order id when mode = "existing-order". */
  transferOrderId: number | null;
  requestIds: string[];
  shipByDate: string;
  quantityOverrides: Record<string, string>;
}

export interface NewTransferDraft {
  origin_location_id: string;
  destination_location_id: string;
  ship_by_date: string;
  default_line_reason: MrpTransferReason;
  notes: string;
  lines: { sku: string; quantity: string; reason: MrpTransferReason }[];
}

export interface EditPendingOrderLineDraft {
  id: number | null;
  sku: string;
  quantity: string;
  reason: MrpTransferReason;
  lockedSku: boolean;
  sourceLabel: string;
  allocatedQuantity: number;
}

export interface EditPendingOrderDraft {
  orderId: number;
  origin_location_id: number;
  destination_location_id: number;
  transferNumber: string | null;
  ship_by_date: string;
  default_line_reason: MrpTransferReason;
  notes: string;
  lines: EditPendingOrderLineDraft[];
  pendingPackQtyRequestCount: number;
}

export type MoveLineTargetMode = "existing" | "new";

export interface MovePendingLineDialogState {
  orderId: number;
  transferNumber: string | null;
  origin_location_id: number;
  destination_location_id: number;
  line: EditPendingOrderLineDraft;
  targetMode: MoveLineTargetMode;
  target_transfer_order_id: string;
  ship_by_date: string;
  notes: string;
}

export const EMPTY_NEW_TRANSFER_DRAFT: NewTransferDraft = {
  origin_location_id: "",
  destination_location_id: "",
  ship_by_date: "",
  default_line_reason: transferReasonDefault(),
  notes: "",
  lines: [],
};

export interface ReceiveDraftLine {
  transfer_order_item_id: number;
  sku_id: string | null;
  expected_qty: number;
  already_received_qty: number;
  already_return_qty: number;
  already_lost_qty: number;
  received_qty: string;
  return_to_origin_qty: string;
  lost_qty: string;
  notes: string;
}

export const RECEIVE_DIALOG_HOVER_HINT =
  "Enter quantity received, returned to origin, and lost for this event. Remaining is what is still in transit after those amounts. Submit moves received stock from in-transit to destination, returns the return quantity to origin, and posts wastage- for lost quantity. When Create new transfer is on, a new pending transfer is created for the returned and lost quantities on the same origin and destination. The transfer moves to Received once remaining is zero on every line; otherwise it stays Partially received.";

export function parseReceiveDraftQty(raw: string): number {
  const trimmed = raw.trim();
  if (trimmed === "") return 0;
  const parsed = Number.parseInt(trimmed, 10);
  return Number.isInteger(parsed) ? parsed : Number.NaN;
}

export function transferReceiveDraftRemaining(line: ReceiveDraftLine): number {
  const receivedQty = parseReceiveDraftQty(line.received_qty);
  const returnQty = parseReceiveDraftQty(line.return_to_origin_qty);
  const lostQty = parseReceiveDraftQty(line.lost_qty);
  const received = Number.isFinite(receivedQty) ? receivedQty : 0;
  const returned = Number.isFinite(returnQty) ? returnQty : 0;
  const lost = Number.isFinite(lostQty) ? lostQty : 0;
  return (
    line.expected_qty
    - line.already_received_qty
    - line.already_return_qty
    - line.already_lost_qty
    - received
    - returned
    - lost
  );
}

export interface ReceiveDialogState {
  transferOrderId: number;
  transferNumber: string | null;
  notes: string;
  createFollowUpTransfer: boolean;
  lines: ReceiveDraftLine[];
}

export const DRAG_MIME = "application/x-transfer-request-id";

/** Sentinel value for the overdue target-month filter (target date before today). */
export const REQUEST_TARGET_DATE_OVERDUE = "__overdue__";

export const FILTER_SELECT_CLASS =
  "h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900 shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500";

export function filterTogglePillClass(active: boolean): string {
  return cn(
    "rounded-full border px-3 py-1 text-xs font-medium transition",
    active
      ? "border-brand-500 bg-brand-50 text-brand-800"
      : "border-slate-300 bg-white text-slate-500 hover:border-slate-400",
  );
}

/** Single-select filter pills: click active tag to clear (show all); click inactive to select only that value. */
export function toggleSingleSelectFilter<T>(current: T | null, value: T): T | null {
  return current === value ? null : value;
}

export type TransferOrderSortKey =
  | "status"
  | "transfer_number_desc"
  | "transfer_number_asc"
  | "ship_by_date_asc"
  | "ship_by_date_desc"
  | "created_desc";

export const TRANSFER_ORDER_SORT_OPTIONS: ReadonlyArray<{ value: TransferOrderSortKey; label: string }> = [
  { value: "status", label: "Status (pending first)" },
  { value: "transfer_number_desc", label: "Transfer number (newest)" },
  { value: "transfer_number_asc", label: "Transfer number (oldest)" },
  { value: "ship_by_date_asc", label: "Ship by date (soonest)" },
  { value: "ship_by_date_desc", label: "Ship by date (latest)" },
  { value: "created_desc", label: "Created (newest)" },
];

export function transferOrderStatusSortRank(status: TransferOrderStatus): number {
  switch (status) {
    case "pending":
      return 0;
    case "packing":
      return 1;
    case "ready_to_ship":
      return 2;
    case "shipped":
      return 3;
    case "delivered":
      return 4;
    case "partially_received":
      return 5;
    case "received":
      return 6;
    case "cancelled":
      return 7;
    default:
      return 99;
  }
}

export function transferNumberSortValue(transferNumber: string | null, fallbackId: number): number {
  const match = transferNumber?.trim().match(/^T-(\d+)/i);
  if (match != null) {
    const parsed = Number.parseInt(match[1]!, 10);
    if (Number.isFinite(parsed)) return parsed;
  }
  return fallbackId;
}

function compareNullableIsoDates(left: string | null, right: string | null): number {
  const leftValue = left?.trim() ?? "";
  const rightValue = right?.trim() ?? "";
  if (leftValue === "" && rightValue === "") return 0;
  if (leftValue === "") return 1;
  if (rightValue === "") return -1;
  return leftValue.localeCompare(rightValue);
}

export function compareTransferBoardOrders(
  left: TransferBoardOrder,
  right: TransferBoardOrder,
  sortKey: TransferOrderSortKey,
): number {
  switch (sortKey) {
    case "status": {
      const statusDelta =
        transferOrderStatusSortRank(left.status) - transferOrderStatusSortRank(right.status);
      if (statusDelta !== 0) return statusDelta;
      return transferNumberSortValue(right.transfer_number, right.id)
        - transferNumberSortValue(left.transfer_number, left.id);
    }
    case "transfer_number_desc":
      return transferNumberSortValue(right.transfer_number, right.id)
        - transferNumberSortValue(left.transfer_number, left.id);
    case "transfer_number_asc":
      return transferNumberSortValue(left.transfer_number, left.id)
        - transferNumberSortValue(right.transfer_number, right.id);
    case "ship_by_date_asc": {
      const dateDelta = compareNullableIsoDates(left.ship_by_date, right.ship_by_date);
      if (dateDelta !== 0) return dateDelta;
      return right.id - left.id;
    }
    case "ship_by_date_desc": {
      const dateDelta = compareNullableIsoDates(right.ship_by_date, left.ship_by_date);
      if (dateDelta !== 0) return dateDelta;
      return right.id - left.id;
    }
    case "created_desc": {
      const createdDelta = right.created_at.localeCompare(left.created_at);
      if (createdDelta !== 0) return createdDelta;
      return right.id - left.id;
    }
    default:
      return 0;
  }
}

export function todayIsoYyyyMmDd(): string {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function isRequestTargetDateOverdue(targetDate: string): boolean {
  return targetDate < todayIsoYyyyMmDd();
}

export function targetDateToMonthKey(targetDate: string): string | null {
  const trimmed = targetDate.trim();
  if (trimmed.length >= 7 && trimmed[4] === "-") {
    return trimmed.slice(0, 7);
  }
  return null;
}

export function formatTransferRequestTargetMonthLabel(monthKey: string): string {
  const parsed = new Date(`${monthKey}-01T12:00:00`);
  if (Number.isNaN(parsed.getTime())) return monthKey;
  return parsed.toLocaleDateString(undefined, { month: "long", year: "numeric" });
}

export function transferRequestSearchHaystack(
  row: TransferBoardRequest,
  reasonLabel: (reason: string) => string,
  hint: MrpSkuHoverHintFields | undefined,
): string {
  return [
    row.sku_id,
    row.notes ?? "",
    reasonLabel(row.reason),
    hint?.description ?? "",
    hint?.variant ?? "",
    hint?.category ?? "",
  ]
    .join(" ")
    .toLowerCase();
}

export function TransferSkuHoverCell({
  sku,
  hint,
  hintsLoading,
  onHoverIntent,
  className,
  extraHint,
}: {
  sku: string;
  hint: MrpSkuHoverHintFields | undefined;
  hintsLoading: boolean;
  onHoverIntent: () => void;
  className?: string;
  extraHint?: ReactNode;
}): JSX.Element {
  const trimmed = sku.trim();
  if (trimmed === "") {
    return <span className="text-slate-400">—</span>;
  }
  return (
    <HoverHintTrigger
      align="start"
      trigger={
        <span
          className={cn(
            "cursor-default font-mono text-xs font-medium text-sky-700 hover:text-sky-900",
            className,
          )}
          onPointerEnter={onHoverIntent}
          onFocus={onHoverIntent}
        >
          <MrpSkuWithUnitLabel sku={trimmed} unit={hint?.mv_unit} />
        </span>
      }
    >
      <div className="space-y-2">
        {extraHint != null ? extraHint : null}
        <MrpSkuCatalogHoverHintBody sku={trimmed} hint={hint} loading={hintsLoading} />
      </div>
    </HoverHintTrigger>
  );
}

export function transferRequestCreatedByLabel(row: TransferBoardRequest): string {
  return (row.created_by_display_name ?? "").trim() || row.created_by.trim();
}

export function transferRequestHasReasonDetails(row: TransferBoardRequest): boolean {
  const notes = row.notes?.trim() ?? "";
  return transferRequestCreatedByLabel(row) !== "" || notes !== "";
}

export function TransferRequestReasonDetail({
  row,
  reasonLabel,
}: {
  row: TransferBoardRequest;
  reasonLabel: (reason: string) => string;
}): JSX.Element {
  const createdBy = transferRequestCreatedByLabel(row);
  const notes = row.notes?.trim() ?? "";
  return (
    <div className="max-w-[min(18rem,calc(100vw-2rem))] space-y-1.5 text-left text-xs leading-snug">
      <p className="text-slate-800">{reasonLabel(row.reason)}</p>
      {createdBy !== "" ? (
        <p>
          <span className="text-slate-500">Created by </span>
          <span className="text-slate-800">{createdBy}</span>
        </p>
      ) : null}
      {notes !== "" ? (
        <p className="whitespace-pre-wrap break-words text-slate-800">{notes}</p>
      ) : null}
    </div>
  );
}

export function laneKeyForRequest(row: TransferBoardRequest): string {
  return `${String(row.origin_location_id)}->${String(row.destination_location_id)}`;
}

export function laneKeyForOrder(row: TransferBoardOrder): string {
  return `${String(row.origin_location_id)}->${String(row.destination_location_id)}`;
}

export function listPendingTransferMoveTargets(
  orders: readonly TransferBoardOrder[],
  sourceOrderId: number,
  originLocationId: number,
  destinationLocationId: number,
): TransferBoardOrder[] {
  const laneKey = `${String(originLocationId)}->${String(destinationLocationId)}`;
  return orders.filter(
    (order) =>
      order.status === "pending"
      && order.id !== sourceOrderId
      && laneKeyForOrder(order) === laneKey,
  );
}

export const DEFAULT_VISIBLE_STATUSES: ReadonlySet<TransferOrderStatus> = new Set(
  TRANSFER_ORDER_STATUS_VALUES.filter((status) => !TRANSFER_ORDER_TERMINAL_STATUSES.has(status)),
);

/** Server allows packing → pending only (no Megaventory side effects yet). */
export function revertToPendingActionFor(
  status: TransferOrderStatus,
): { to: TransferOrderStatus; label: string } | null {
  if (status === "packing") {
    return { to: "pending", label: "Revert to pending" };
  }
  return null;
}
